import React, {useEffect} from 'react';
import {classNames} from '@utils/class-names';
import {ReactComponent as SearchIcon} from "@assets/icons/search.svg";
import {XMarkIcon} from "@heroicons/react/20/solid";
import {useSearchBar} from "@common/hooks/use-search-bar.hook";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {ArticleListResponse} from "../../core/models/interfaces/article-list-response.interface";
import {ArticleListRequestParams} from "../../core/models/interfaces/article-list-request-params.interface";
import AppButton from "@common/components/app-button";

interface ArticleSearchbarProps {
    placeholder?: string;
    onResults?: (results: ArticleListResponse) => void;
    searchParams?: ArticleListRequestParams;
}

const ArticleSearchbar: React.FC<ArticleSearchbarProps> = (props: ArticleSearchbarProps) => {
    const {placeholder = 'Szukaj', onResults, searchParams = {}} = props;
    const {getSearchArticles} = useArticleApi();
    
    const searchCallback = (searchQuery: string) => getSearchArticles({
        ...searchParams,
        title: searchQuery
    });
    
    const {searchQuery, setSearchQuery, clearSearch} = useSearchBar<ArticleListResponse>(
        searchCallback,
        {
            debounceMs: 250,
            minChars: 0,
            onSuccess: (res) => onResults?.(res ?? []),
            skipFirstLoading: true,
        }
    );

    useEffect(() => {
        if (searchQuery) {
            searchCallback(searchQuery);
        }
    }, [searchParams.visibility, searchParams.sortBy, searchParams.sortOrder]);

    const inputChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e): void => {
        setSearchQuery(e.target.value);
    }

    const clearHandler = (): void => {
        clearSearch();
    }

    return (
        <div className="relative w-full">
            <SearchIcon
                className="absolute top-[10px] left-[15px] flex items-center pointer-events-none h-5 w-5 stroke-2"/>
            <input
                type="text"
                value={searchQuery}
                onChange={inputChangeHandler}
                placeholder={placeholder}
                className={classNames(
                    "w-full pl-12 pr-10 h-[40px]",
                    "bg-input-background/5 rounded-[4rem] font-normal",
                    "placeholder:text-on-surface text-base placeholder:text-base placeholder:font-normal",
                    "transition-colors duration-200 outline-primary/60",
                    "text-sm"
                )}
            />
            {searchQuery && (
                <AppButton
                    type="button"
                    onClick={clearHandler}
                    aria-label="Wyczyść wyszukiwanie"
                    className={classNames(
                        "!absolute !top-[6px] !right-[10px] !h-6 !w-6 !p-0",
                        "!bg-transparent !border-0 text-on-surface hover:text-primary"
                    )}
                    classNameContentWrapper="!p-0"
                >
                    <XMarkIcon className="h-5 w-5 stroke-2"/>
                </AppButton>
            )}
        </div>
    );
};

export default ArticleSearchbar;
